import { IProdutoPedido } from "../interface/IProdutoPedido";
import { TipoProduto } from "../../produtos/enum/TipoProduto";
import { calcularPrecoComplemento } from "./calcularPrecoComplemento";

/**
 * Calcula o valor total de um produto considerando:
 * - Preço base do produto
 * - Para pizzas: borda, massa, sabores (com lógica de precificação) e valor adicional
 * - Para produtos preparados: complementos e valor adicional
 * - Para todos: itens (quando disponível, já processados)
 */
export const calcularValorTotalProduto = (produto: IProdutoPedido): number => {
    // Se o produto já tem itens processados, usa eles (mais confiável)
    if (produto.itens && produto.itens.length > 0) {
        let total = produto.preco ?? 0;
        produto.itens.forEach(item => {
            total += (item.preco ?? 0) * (item.quantidade ?? 1);
        });
        return total;
    }

    // Caso contrário, calcula manualmente
    let total = produto.preco ?? 0;

    // Para pizzas
    if (produto.tipo === TipoProduto.pizza) {
        // Para pizzas sem itens processados, calculamos apenas os componentes
        // O produto.preco pode ser 0 ou não confiável neste caso
        // Calcula valor dos sabores baseado no modelo de precificação
        let valorSabores = 0;
        if (produto.sabores && produto.sabores.length > 0) {
            const modeloPrecificacao = produto.configuracao?.modeloPrecificacao;

            if (modeloPrecificacao === "precoDividido") {
                // Soma todos os sabores e divide pelo número de sabores
                let somaSabores = 0;
                produto.sabores.forEach(sabor => {
                    somaSabores += sabor.preco ?? 0;
                });
                valorSabores = Math.ceil(somaSabores / produto.sabores.length);
            } else if (modeloPrecificacao === "maiorPreco") {
                // Pega o sabor mais caro
                const saborMaisCaro = produto.sabores.reduce((maior, atual) => 
                    (maior.preco ?? 0) > (atual.preco ?? 0) ? maior : atual
                );
                valorSabores = saborMaisCaro.preco ?? 0;
            } else if (modeloPrecificacao === "precoMisto") {
                // Se mais de 2 sabores, usa maior preço; senão, usa preço dividido
                if (produto.sabores.length > 2) {
                    const saborMaisCaro = produto.sabores.reduce((maior, atual) => 
                        (maior.preco ?? 0) > (atual.preco ?? 0) ? maior : atual
                    );
                    valorSabores = saborMaisCaro.preco ?? 0;
                } else {
                    let somaSabores = 0;
                    produto.sabores.forEach(sabor => {
                        somaSabores += sabor.preco ?? 0;
                    });
                    valorSabores = Math.ceil(somaSabores / produto.sabores.length);
                }
            } else {
                // Se não tem modelo de precificação definido, soma todos os sabores
                produto.sabores.forEach(sabor => {
                    valorSabores += sabor.preco ?? 0;
                });
            }
        }

        // Para pizzas sem itens, o total é: sabores + borda + massa + valorAdicional
        // Usamos o maior entre produto.preco e valorSabores para garantir que não perdemos o preço base se existir
        total = Math.max(produto.preco ?? 0, valorSabores);
        total += (produto.borda?.preco ?? 0);
        total += (produto.massa?.preco ?? 0);
        total += (produto.valorAdicional ?? 0);
    }

    // Para produtos preparados
    if (produto.tipo === TipoProduto.preparado) {
        // Adiciona valor dos complementos respeitando a regra de precificação de cada grupo
        if (produto.complementos && produto.complementos.length > 0) {
            produto.complementos.forEach(complemento => {
                total += calcularPrecoComplemento(complemento);
            });
        }

        // Adiciona valor adicional
        if (produto.valorAdicional) {
            total += produto.valorAdicional;
        }
    }

    return total;
};

